/** Mention is a session the user @mentioned in a message: its name as written and its session id. */
export interface Mention {
  name: string;
  session: string;
}

/** mentionName is how a session is written after an `@`: under its parent's name when another session started it. */
export function mentionName(label: string, parentLabel: string | undefined): string {
  return parentLabel ? `${parentLabel}/${label}` : label;
}

/** mentionQuery is the `@word` being typed just before the caret, or null when the caret is not in one. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = /(?:^|\s)@([^\s@]*)$/.exec(before);
  if (!m) return null;
  return { start: caret - m[1]!.length - 1, query: m[1]! };
}

/** mentionCandidates are the sessions whose name holds the query, names starting with it first. */
export function mentionCandidates(all: Mention[], query: string, max = 6): Mention[] {
  const q = query.toLowerCase();
  return all
    .filter((m) => m.name.toLowerCase().includes(q))
    .sort((a, b) => Number(!a.name.toLowerCase().startsWith(q)) - Number(!b.name.toLowerCase().startsWith(q)) || a.name.localeCompare(b.name))
    .slice(0, max);
}

/** insertMention replaces the `@word` at `start` with the mention, returning the new text and caret. */
export function insertMention(text: string, start: number, caret: number, m: Mention): { text: string; caret: number } {
  const rest = text.slice(caret);
  const token = `@${m.name}`;
  const gap = /^\s/.test(rest) ? "" : " ";
  return { text: text.slice(0, start) + token + gap + rest, caret: start + token.length + 1 };
}

/** mentionsIn keeps the picked mentions the text still carries, each once. */
export function mentionsIn(text: string, picked: Mention[]): Mention[] {
  const seen = new Set<string>();
  return picked.filter((m) => text.includes(`@${m.name}`) && !seen.has(m.session) && seen.add(m.session));
}

/**
 * linkMentions marks each `@name` of a known session in rendered message HTML as a link carrying that
 * session's id in `data-session`; text inside code and existing links is left alone.
 */
export function linkMentions(html: string, targets: Mention[]): string {
  if (targets.length === 0 || !html.includes("@")) return html;
  const byName = new Map(targets.map((t) => [t.name, t.session]));
  const names = [...byName.keys()].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(^|[\\s(])@(${names.join("|")})(?![\\w/-])`, "g");
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
  const hits: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!(n.parentElement?.closest("code, pre, a")) && n.nodeValue?.includes("@")) hits.push(n as Text);
  }
  for (const node of hits) {
    const text = node.nodeValue!;
    re.lastIndex = 0;
    if (!re.test(text)) continue;
    re.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let at = 0;
    for (let m = re.exec(text); m; m = re.exec(text)) {
      const start = m.index + m[1]!.length;
      frag.append(text.slice(at, start));
      const a = document.createElement("a");
      a.className = "mention";
      a.href = "#";
      a.dataset.session = byName.get(m[2]!)!;
      a.textContent = `@${m[2]}`;
      frag.append(a);
      at = start + 1 + m[2]!.length;
    }
    frag.append(text.slice(at));
    node.replaceWith(frag);
  }
  return tpl.innerHTML;
}
